import React, { useState } from 'react';
import chemicalService from '../../../services/chemicalService';

// mode: 'expense' (reimbursable → +credit) or 'advance' (drawn → -debit)
const AgentEntryModal = ({ agent, mode, onClose, onSaved }) => {
  const isExpense = mode === 'expense';
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    description: '',
    entry_date: new Date().toISOString().split('T')[0],
  });

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return alert('❌ Enter a valid amount.');
    if (!form.entry_date) return alert('❌ Date is required.');

    try {
      setSubmitting(true);
      await chemicalService.addAgentEntry({
        agent_id: agent.id,
        entry_type: mode,
        amount: amt,
        description: form.description.trim() || null,
        entry_date: form.entry_date,
      });
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save entry';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const accent = isExpense ? '#27ae60' : '#e74c3c';

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={{ ...styles.modalTitle, color: accent }}>
              {isExpense ? '+ Add Reimbursable Expense' : '− Add Advance'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isExpense
                ? `${agent.name} paid from his pocket — SwissChem owes it back (credit)`
                : `Cash ${agent.name} has drawn — deducted from his share (debit)`}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Amount <span style={styles.required}>*</span></label>
              <input
                type="number" step="0.01" min="0" value={form.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                style={styles.input} placeholder="0" autoFocus
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Date <span style={styles.required}>*</span></label>
              <input
                type="date" value={form.entry_date}
                onChange={(e) => handleChange('entry_date', e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                style={{ ...styles.input, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder={isExpense ? 'e.g. Client entertainment, fuel...' : 'e.g. Cash advance'}
                rows="2"
              />
            </div>
          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>Cancel</button>
            <button type="submit" style={{ ...styles.submitButton, background: accent }} disabled={submitting}>
              {submitting ? '⏳ Saving...' : isExpense ? '✓ Add Expense' : '✓ Add Advance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '480px', maxHeight: '88vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  modalTitle: { fontSize: '20px', fontWeight: '700', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { padding: '12px 28px', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default AgentEntryModal;